import React, { useEffect, useState, useRef } from "react";
import { Search } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SearchSection = () => {

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [recentSearch, setRecentSearch] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  // ✅ Highlight text
  const highlightText = (text, keyword) => {
    if (!keyword) return text;

    const regex = new RegExp(`(${keyword})`, "gi");

    return text.split(regex).map((part, index) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <span key={index} className="text-teal-600 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // ✅ Load recent search
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("recentSearch")) || [];
    setRecentSearch(data);
  }, []);

  // ✅ Debounce API
  useEffect(() => {
    const delay = setTimeout(() => {
      if (search.length > 1) {
        fetchSearch();
      } else {
        setResults([]);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [search]);

  const fetchSearch = async () => {
    try {
      const res = await axios.get(
        `http://localhost:5000/api/search?keyword=${search}`
      );
      setResults(res.data.products || []);
    } catch (err) {
      console.log(err);
    }
  };

  // ✅ Search click
  const handleSearch = () => {
    if (!search) return;

    let recent = JSON.parse(localStorage.getItem("recentSearch")) || [];

    recent = recent.filter((item) => item !== search);
    recent.unshift(search);

    if (recent.length > 5) recent.pop();

    localStorage.setItem("recentSearch", JSON.stringify(recent));
    setRecentSearch(recent);

    navigate(`/searchproduct?keyword=${search}`);
    setShowDropdown(false);
  };

  // ✅ Keyboard navigation
  const handleKeyDown = (e) => {
    const list = search.length > 0 ? results : recentSearch;
    const total = list.length;

    if (total === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % total);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev <= 0 ? total - 1 : prev - 1
      );
    }

    if (e.key === "Enter") {
      e.preventDefault();

      if (search.length > 0 && results[activeIndex]) {
        navigate(`/product/${results[activeIndex]._id}`);
      } else if (recentSearch[activeIndex]) {
        navigate(`/searchproduct?keyword=${recentSearch[activeIndex]}`);
      }

      setShowDropdown(false);
    }
  };

  // ✅ Reset index
  useEffect(() => {
    setActiveIndex(-1);
  }, [search]);

  // ✅ Click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full bg-white py-8">
      <div className="max-w-3xl mx-auto px-4" ref={wrapperRef}>

        <div className="relative flex items-center border rounded-full bg-white">

          <div className="pl-4 text-gray-400">
            <Search size={18} />
          </div>

          <input
            type="text"
            value={search}
            onFocus={() => setShowDropdown(true)}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search products..."
            className="w-full px-3 py-3 outline-none"
          />

          <button
            onClick={handleSearch}
            className="bg-teal-600 text-white px-6 py-3 rounded-r-full"
          >
            Search
          </button>

          {/* 🔽 DROPDOWN */}
          {showDropdown && (
            <div className="absolute top-full left-0 w-full bg-white shadow-2xl rounded-xl mt-2 z-[999] border max-h-96 overflow-y-auto">

              {/* Recent */}
              {search.length === 0 && recentSearch.length > 0 && (
                <>
                  <div className="px-4 py-2 text-gray-400 text-xs border-b">
                    Recent Searches
                  </div>

                  {recentSearch.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        navigate(`/searchproduct?keyword=${item}`);
                        setShowDropdown(false);
                      }}
                      className={`px-4 py-3 cursor-pointer
                        ${activeIndex === index ? "bg-gray-200" : "hover:bg-gray-100"}
                      `}
                    >
                      🔍 {highlightText(item, search)}
                    </div>
                  ))}
                </>
              )}

              {/* Products */}
              {search.length > 0 && (
                <>
                  <div className="px-4 py-2 text-gray-400 text-xs border-b">
                    Products
                  </div>

                  {results.length > 0 ? (
                    results.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          navigate(`/product/${item._id}`);
                          setShowDropdown(false);
                        }}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer
                          ${activeIndex === index ? "bg-gray-200" : "hover:bg-gray-100"}
                        `}
                      >
                        <img
                          src={item.product_img}
                          alt=""
                          className="w-12 h-12 object-cover rounded"
                        />

                        <div>
                          <p className="text-sm">
                            {highlightText(item.product_name, search)}
                          </p>
                          <p className="text-xs text-gray-500">
                            ₹{item.sell_price}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-400">
                      No products found
                    </div>
                  )}
                </>
              )}

              {/* Clear */}
              {recentSearch.length > 0 && (
                <div
                  onClick={() => {
                    localStorage.removeItem("recentSearch");
                    setRecentSearch([]);
                  }}
                  className="text-center text-red-500 py-3 border-t cursor-pointer"
                >
                  Clear History
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SearchSection;